import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router';
import { Icon, Spinner } from './ui/Icon.js';
import { Button } from './ui/Button.js';
import { apiFetch } from '../lib/api.js';

type Phase = 'chat' | 'summary' | 'submitted' | 'error';

interface FeedbackSummary {
  category: 'bug' | 'feature_request' | 'ux_issue' | 'general';
  title: string;
  description: string;
  reproduction_steps: string | null;
  severity: 'critical' | 'high' | 'medium' | 'low' | null;
}

interface FeedbackMessage {
  role: 'user' | 'assistant';
  content: string;
  ts: number;
  phase?: Phase;
  summary?: FeedbackSummary | null;
  submittedId?: string;
}

interface FeedbackState {
  sessionId: string | null;
  messages: FeedbackMessage[];
  /** Once true, the session is frozen and the user must reset to continue. */
  submitted: boolean;
}

const STORAGE_KEY = 'beacon-feedback-session';

const GREETING: FeedbackMessage = {
  role: 'assistant',
  content:
    "Hi — I'm the GEO Observatory feedback agent. Describe an issue, ask a question, or share what's working well. I'll triage and follow up if I need more detail.",
  ts: Date.now(),
};

const FRESH_STATE: FeedbackState = { sessionId: null, messages: [GREETING], submitted: false };

function loadState(): FeedbackState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return FRESH_STATE;
    const parsed = JSON.parse(raw) as FeedbackState;
    if (!Array.isArray(parsed.messages) || parsed.messages.length === 0) return FRESH_STATE;
    return parsed;
  } catch {
    return FRESH_STATE;
  }
}

function saveState(state: FeedbackState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* non-fatal */
  }
}

const CATEGORY_COLORS: Record<FeedbackSummary['category'], string> = {
  bug: 'bg-red-100 text-red-700',
  feature_request: 'bg-blue-100 text-blue-700',
  ux_issue: 'bg-amber-100 text-amber-700',
  general: 'bg-slate-100 text-slate-700',
};

/** Sentence case (spec 255) — CSS `capitalize` would title-case "feature request". */
const CATEGORY_LABELS: Record<FeedbackSummary['category'], string> = {
  bug: 'Bug',
  feature_request: 'Feature request',
  ux_issue: 'UX issue',
  general: 'General',
};

const SEVERITY_COLORS: Record<NonNullable<FeedbackSummary['severity']>, string> = {
  critical: 'bg-red-600 text-white',
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-slate-100 text-slate-600',
};

function SummaryCard({
  summary,
  disabled,
  onConfirm,
  onRequestChanges,
  confirming,
}: {
  summary: FeedbackSummary;
  disabled: boolean;
  onConfirm: () => void;
  onRequestChanges: () => void;
  confirming: boolean;
}) {
  const steps = summary.reproduction_steps
    ? summary.reproduction_steps.split('\n').map((s) => s.trim()).filter(Boolean)
    : [];

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3.5" data-testid="feedback-summary-card">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${CATEGORY_COLORS[summary.category]}`}>
          {CATEGORY_LABELS[summary.category]}
        </span>
        {summary.severity && (
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${SEVERITY_COLORS[summary.severity]}`}>
            {summary.severity}
          </span>
        )}
      </div>
      <h3 className="mt-2 text-sm font-semibold text-slate-900">{summary.title}</h3>
      <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{summary.description}</p>
      {steps.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold text-slate-700">Reproduction steps</p>
          <ol className="mt-1 list-decimal pl-4 text-xs text-slate-600 space-y-0.5">
            {steps.map((step, i) => (
              <li key={i}>{step.replace(/^\d+\.\s*/, '')}</li>
            ))}
          </ol>
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <Button
          variant="primary"
          className="flex-1 justify-center"
          onClick={onConfirm}
          disabled={disabled || confirming}
          data-testid="feedback-confirm-submit"
        >
          {confirming ? <Spinner /> : <Icon name="bx-check" />}
          Confirm &amp; submit
        </Button>
        <Button
          variant="ghost"
          className="flex-1 justify-center"
          onClick={onRequestChanges}
          disabled={disabled || confirming}
          data-testid="feedback-request-changes"
        >
          <Icon name="bx-edit" />
          Request changes
        </Button>
      </div>
    </div>
  );
}

export function FeedbackWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<FeedbackState>(() => loadState());
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    saveState(state);
  }, [state]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, state.messages.length]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const send = useCallback(async () => {
    const trimmed = draft.trim();
    if (!trimmed || sending || state.submitted) return;

    const userMsg: FeedbackMessage = { role: 'user', content: trimmed, ts: Date.now() };
    const nextMessages = [...state.messages, userMsg];
    setState((s) => ({ ...s, messages: nextMessages }));
    setDraft('');
    setSending(true);
    setError(null);

    try {
      // Spec 259: Feedback Agent 0.3.0 stores page_url, browser_ua and app_id,
      // and answers 422 to anything else — `brand` is no longer sent (the
      // server also builds the context from that allow-list).
      const post = (sessionId: string | null | undefined) => apiFetch('/api/v1/feedback', {
        method: 'POST',
        body: JSON.stringify({
          message: trimmed,
          sessionId: sessionId ?? undefined,
          context: {
            page_url: typeof window !== 'undefined' ? window.location.href : location.pathname,
            browser_ua: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
          },
        }),
      });
      let res = await post(state.sessionId);
      // A session belongs to the user who started it: a different signed-in
      // user on the same session id is refused (403 reporter_mismatch). Their
      // message starts a session of their own instead of failing.
      if (res.status === 403 && state.sessionId) {
        const why = await res.clone().text().catch(() => '');
        if (why.includes('reporter_mismatch')) res = await post(null);
      }
      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        throw new Error(errBody?.error ?? `Feedback request failed (${res.status})`);
      }
      const data = (await res.json()) as {
        session_id: string;
        message: string;
        phase: Phase;
        summary: FeedbackSummary | null;
      };
      const replyMsg: FeedbackMessage = {
        role: 'assistant',
        content: data.message || '(no reply)',
        ts: Date.now(),
        phase: data.phase,
        summary: data.summary,
      };
      setState({
        sessionId: data.session_id,
        messages: [...nextMessages, replyMsg],
        submitted: false,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send feedback');
    } finally {
      setSending(false);
    }
  }, [draft, sending, state, location.pathname]);

  const confirmSubmit = useCallback(async () => {
    if (!state.sessionId || confirming) return;
    setConfirming(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/feedback/submit', {
        method: 'POST',
        body: JSON.stringify({ sessionId: state.sessionId }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        throw new Error(errBody?.error ?? `Submit failed (${res.status})`);
      }
      const data = (await res.json()) as { feedback_item: { id: string } };
      const successMsg: FeedbackMessage = {
        role: 'assistant',
        content: 'Thanks — your feedback has been submitted. A human will review it shortly.',
        ts: Date.now(),
        phase: 'submitted',
        submittedId: data.feedback_item?.id,
      };
      setState((s) => ({ ...s, messages: [...s.messages, successMsg], submitted: true }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    } finally {
      setConfirming(false);
    }
  }, [state.sessionId, confirming]);

  const requestChanges = useCallback(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  const reset = () => {
    setState(FRESH_STATE);
    setError(null);
    setDraft('');
  };

  return (
    <>
      {!open && (
        // Spec 247 — Kridt's Help button: one round INK button, 48px,
        // bx-message-rounded-dots, labelled "Feedback", the same in all seven
        // hub apps. It was violet with a drop shadow; Kridt has no shadows, and
        // the review found four help-button styles across the family.
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Feedback"
          data-testid="feedback-launcher"
          className="hub-help"
        >
          <Icon name="bx-message-rounded-dots" />
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-5 right-5 z-40 flex h-[600px] w-[400px] max-w-[calc(100vw-2.5rem)] flex-col rounded-lg border border-slate-200 bg-white"
          data-testid="feedback-drawer"
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <div className="flex items-center gap-2">
              <Icon name="bx-message-rounded" />
              <h2 className="text-sm font-semibold text-slate-900">Feedback</h2>
              {state.submitted && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                  <Icon name="bx-check-circle" /> Submitted
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={reset}
                aria-label="Start new conversation"
                title="Start new conversation"
                className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                data-testid="feedback-reset"
              >
                <Icon name="bx-trash" />
              </button>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close feedback"
                className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                data-testid="feedback-close"
              >
                <Icon name="bx-x" />
              </button>
            </div>
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
            data-testid="feedback-messages"
          >
            {state.messages.map((msg, i) => {
              const isLast = i === state.messages.length - 1;
              const isSummary = msg.phase === 'summary' && msg.summary;
              return (
                <div key={i} className="space-y-2">
                  <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                        msg.role === 'user' ? 'bg-[var(--hub-color-ink)] text-[var(--hub-color-ink-on-ink)]' : 'bg-[var(--hub-color-surface-sunken)] text-[var(--hub-color-ink)]'
                      }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                  {isSummary && msg.summary && (
                    <SummaryCard
                      summary={msg.summary}
                      disabled={!isLast || state.submitted}
                      onConfirm={confirmSubmit}
                      onRequestChanges={requestChanges}
                      confirming={confirming}
                    />
                  )}
                </div>
              );
            })}
            {sending && (
              <div className="flex justify-start">
                <div className="rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-500">
                  <Spinner className="inline" /> Thinking...
                </div>
              </div>
            )}
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {error}
              </div>
            )}
          </div>

          <div className="border-t border-slate-100 px-3 py-2.5">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={state.submitted ? 'Session closed — click trash to start a new one' : 'Describe the issue or ask a question...'}
                rows={2}
                disabled={sending || state.submitted}
                className="flex-1 resize-none rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500 disabled:bg-slate-50"
                data-testid="feedback-input"
              />
              <button
                onClick={() => void send()}
                disabled={sending || state.submitted || !draft.trim()}
                aria-label="Send feedback"
                className="hub-btn hub-btn--primary hub-btn--round shrink-0"
                data-testid="feedback-send"
              >
                {sending ? <Spinner  /> : <Icon name="bx-send" />}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-slate-400">
              Enter to send · Shift+Enter for new line · Context attached automatically
            </p>
          </div>
        </div>
      )}
    </>
  );
}
